<template>
  <div v-if="hasBreakingChanges" class="app-toast is--error is--inline">
    <div class="app-toast__text">
      <p class="app-toast__title">Warning: There has been a new release with breaking change(s)</p>
      <p>Some content, needs to be resaved or updated due to recent changes. Get in contact with your LXA Guide to make a fix.</p>
    </div>
  </div>

  <div class="app-publications-list" v-if="isEditingAllowed">
  <div class="page-main__content">
    <ProjectSourceBanner v-if="showBanner"
      :project="project">
    </ProjectSourceBanner>
    <ProjectSourceBanner v-if="hasContentInErrorState > 0"
      banner-icon="error"
      banner-title="AI Generation Error"
      banner-description="One or more of the courses that you have generated has an error. Select the &quot;?&quot; tooltip for further details."
      :project="project">
    </ProjectSourceBanner>
    <div class="app-filters">
      <div class="input select">
        <select class="" v-model="statusFilter">
          <option value="">Filter by status...</option>
          <option v-for="(label, item) in filterOptions" :value="item" >{{ label }}</option>
        </select>
      </div>
      <div class="app-search">
        <div class="input text">
          <input
            v-model="searchFilter"
            name="search"
            type="search"
            placeholder="Search for courses"
          />
        </div>
      </div>
      <div v-if="aiEnabled" class="app-filters__buttons">
        <a
          class="element-button--ai-primary element-button"
          :href="`/projects/${project.id}/courses/create`">
          <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
          Generate Course with AI
        </a>
      </div>
    </div>

    <div class="element-table__wrapper">
      <table>
        <thead>
          <tr>
            <th>Title
              <div data-bs-toggle="dropdown" class="element-chip is--ai">
                <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
                AI
              </div>
              <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                <h4 class="app-tooltip__title">AI Generated</h4>
                <div class="app-tooltip__body">
                  <p>We’ve used AI to generate this content based on the information provided.</p>
                </div>
              </div>
            </th>
            <th>
              ID
              <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
              <div 
                class="app-tooltip--identifier app-tooltip dropdown-menu">
                <h4 class="app-tooltip__title">ID</h4>
                <div class="app-tooltip__body">
                  Since multiple Courses within a Project can share the same screen ID (e.g., aa010), ID is used to differentiate courses in Issue Tracker and prevent any confusion (e.g., "course010-aa010" vs. "course009-aa010").
                </div>
              </div>
            </th>
            <th>
              Status
              <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
              <div 
                class="app-tooltip--generate-status app-tooltip dropdown-menu">
                <h4 class="app-tooltip__title">Status Definitions</h4>
                <div class="app-tooltip__body">
                  <ul>
                    <li v-for="item in staticStatuses">
                      <strong>{{ item.label }}:</strong> {{ item.description }}
                    </li>
                  </ul>
                </div>
              </div>
            </th>
            <th>Created By</th>
            <th>Created Date</th>
            <th>Last Modified</th>
            <th></th>
          </tr>
        </thead>
        <tbody v-if="filteredPublications.length">
          <tr v-for="item in filteredPublications" :key="item.id">
            <td class="element-bold">
              <div class="app-publications-list__title-cell">
                <span class="element-featured-icon is--sm">
                  <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
                </span>
                {{ item.name }}
              </div>
            </td>
            <td>{{ getCourseId(item) }}</td>
            <td>
              <div v-if="hasProgressBar(item)" class="element-progress">
                <div class="element-progress__text">{{ getPublicationStatusLabel(item) }}</div>
                <div class="element-progress__bar">
                  <div
                    class="element-progress__bar-filled"
                    role="progressbar"
                    aria-valuenow="0"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    :style="`width: ${getProgressPercentage(item)}%;`">
                  </div>
                </div>
              </div>
              <div v-else class="app-publications-list__statuses">
                <span :class="['element-badge', getStatusClass(item)]">{{ getPublicationStatusLabel(item) }}</span>
                <svg v-if="['error'].includes(getStatus(item))" data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                <div 
                  class="app-tooltip--generate-status app-tooltip dropdown-menu">
                  <h4 class="app-tooltip__title">Error</h4>
                  <div class="app-tooltip__body">
                    <p>{{ getPublicationStatusLabel(item, true) }}</p>
                  </div>
                </div>
              </div>
            </td>
            <td>{{ item.created_by.name }}</td>
            <td>{{ localizeDate(item.created_at).format('MMM D, YYYY h:mm A') }}</td>
            <td>{{ localizeDate(item.updated_at).format('MMM D, YYYY h:mm A') }}</td>
            <td class="element-table__actions">
              <template v-if="['in-progress--outline-draft', 'error'].includes(getStatus(item)) && hasOutline(item) && !hasBasicAccess && aiEnabled">
                <a class="element-button--secondary element-button" :href="`/publications/${item.id}/course-outline`">Review Outline</a>
              </template>
              <template v-else-if="['new', 'draft', 'initialized', 'error'].includes(getStatus(item)) && !hasBasicAccess && aiEnabled">
                <a class="element-button--secondary element-button" :href="`/publications/${item.id}/course-sources`">Continue Generating</a>
              </template>
              <template v-else-if="this.isCompleted(item)">
                <a class="element-button--secondary element-button" :href="`/publications/${item.id}/course-outline`">Edit</a>
                <button
                    @click="onPreviewPressed(item)"
                    class="element-button--secondary element-button">
                    Preview
                </button>
                <app-button
                    :key="`${item.id}-word`"
                    class="element-button--secondary element-button"
                    @click="onSharePressed(item)">
                    Share
                </app-button>
              </template>
              <template v-if="isDeleteAllowed(item)">
                <div @click="$event.isAction = true" class="dropdown">
                  <button class="element-table__actions-button element-button--icon element-button" type="button" data-bs-toggle="dropdown">
                      <svg class="element-icon--more-info element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-more-info"></use></svg>
                  </button>
                  <div class="app-dropdown dropdown-menu">
                      <template v-if="this.isCompleted(item)">
                        <app-button
                          :key="`${item.id}-scorm`"
                          @click="onScormPressed(item)"
                          ><svg class="element-icon--scorm element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-scorm"></use></svg>
                          Export SCORM
                        </app-button>
                        <app-button
                          :key="`${item.id}-ppt`"
                          @click="onPPTPressed(item)"
                          ><svg class="element-icon--ppt element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ppt"></use></svg>
                          Export PowerPoint
                        </app-button>
                        <app-button
                          :key="`${item.id}-word`"
                          @click="onWordPressed(item)"
                          ><svg class="element-icon--word element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-word"></use></svg>
                          Export Word
                        </app-button>
                      </template>
                      <app-button
                          class="is--alert"
                          @click="remove(item)">
                          <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                          Delete
                      </app-button>
                  </div>
              </div>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!publications.length" class="empty-table element-empty-instructions">
        <img src="/img/undraw_runner_start_x-0-uu.svg" />
        <p>No courses have been added.</p>
      </div>
    </div>
  </div>
  <publication-export-overlay v-if="exportingWord"
    :publication="exportPublication"
    :exportOptions="wordOptions"
    :exportType="`word`"
    @cancel="onExportOverlayClose()"
    @docGenerated="updatePublication($event)"
    class="page-user-project-publication-export-overlay">
  </publication-export-overlay>
  <publication-export-overlay v-if="exportingPPT"
    :publication="exportPublication"
    :exportOptions="pptOptions"
    :exportType="`ppt`"
    @cancel="onExportOverlayClose()"
    @docGenerated="updatePublication($event)"
    class="page-user-project-publication-export-overlay">
  </publication-export-overlay>
  <publication-export-overlay v-if="exportingScorm"
    :publication="exportPublication"
    :exportOptions="scormOptions"
    :exportType="`scorm`"
    @cancel="onExportOverlayClose()"
    @docGenerated="updatePublication($event)"
    class="page-user-project-publication-export-overlay">
  </publication-export-overlay>
  <publication-share-overlay v-if="sharePublicPreview"
    :publication="exportPublication"
    :project="project"
    :issuetracker-url="issuetrackerUrl"
    @cancel="onExportOverlayClose()"
    class="page-user-project-publication-share-overlay">
  </publication-share-overlay>
</div>

<project-sources-splash v-if="!isEditingAllowed"
  :project="project"
  :project-source="projectSource"
  :has-golden-source="hasGoldenSource"
  :is-analyzing="isAnalyzing">
</project-sources-splash>

</template>

<script>
import _, { trim } from 'lodash'
import Echo from '../helpers/echo'
import api from '../api/publisher'
import { localizeDate } from '../helpers/util'
import PublicationAiGenerate from './PublicationAiGenerate'
import PublicationExportOverlay from './PublicationExportOverlay'
import PublicationShareOverlay from './PublicationShareOverlay'
import ProjectSourceBanner from './ProjectSourceBanner'
import { mapGetters } from 'vuex'

export default {
  components: { PublicationAiGenerate, PublicationExportOverlay, ProjectSourceBanner, PublicationShareOverlay },
  props: {
    wordOptions: {
      type: Object,
    },
    pptOptions: {
      type: Object,
    },
    scormOptions: {
      type: Object,
    },
    shareOptions: {
      type: Object,
    },
    project: {
      type: Object,
    },
    issuetrackerUrl: {
      type: String,
      default: null
    },
    schemaVersions: {
      type: [Object, Array, null],
      default: null
    },
    results: {
      type: Object,
    },
    aiEnabled: {
      type: [Boolean, Number],
      default: false
    },
    showBanner: {
      type: [Boolean, Number],
      default: true
    },
    hasContentInErrorState: {
      type: Number,
      default: 0
    },
    selectedPublicationExport: {
      type: [Number],
      default: null
    }
  },
  watch: {
    results: {
      handler() {
        if (this.selectedPublicationExport) {
            this.selectedPublication = this.publications.find(pub => pub.id === this.selectedPublicationExport)
            this.onPPTPressed(this.selectedPublication);
        }
      },
      immediate: true,
      deep: true
    }
  },
  data() {
    return {
      creatingNew: false,
      exportingWord: false,
      exportingPPT: false,
      exportingScorm: false,
      sharePublicPreview: false,
      projectSource: this.project.project_source || null,
      actionMenu: null,
      statusFilter: "",
      searchFilter: "",
      publications: this.results || [],
      exportOptions: null,
      exportPublicationID: null,
      selectedPublication: null,
    }
  },
  computed: {
    ...mapGetters('builder', [
        'hasBreakingChanges'
    ]),
    ...mapGetters([
      'hasBasicAccess'
    ]),
    isEditingAllowed() {
      return this.hasGoldenSource && !this.isAnalyzing && !this.hasGoldenSourceError
    },
    isAnalyzing() {
      return (this.projectSource && this.projectSource.is_analyzing) ? true : false
    },
    hasGoldenSource() {
      return (this.projectSource && this.projectSource.completed_at) ? true : false 
    },
    hasGoldenSourceError() {
      return (this.projectSource && this.projectSource.errors) ? true : false
    },
    staticStatuses() {
      return [
        { label: this.getStatusLabel('new'), description: this.getStatusLabel('new', true) },
        { label: this.getStatusLabel('in-progress--outline-queued'), description: this.getStatusLabel('in-progress--outline-queued', true) },
        { label: this.getStatusLabel('in-progress--outline-generating'), description: this.getStatusLabel('in-progress--outline-generating', true) },
        { label: this.getStatusLabel('in-progress--outline-draft'), description: this.getStatusLabel('in-progress--outline-draft', true) },
        { label: this.getStatusLabel('in-progress--content-queued'), description: this.getStatusLabel('in-progress--content-queued', true) },
        { label: this.getStatusLabel('in-progress--content-generating'), description: this.getStatusLabel('in-progress--content-generating', true) },
        { label: this.getStatusLabel('completed'), description: this.getStatusLabel('completed', true) },
      ];
    },
    filterOptions() {
      return {
        "draft": this.getStatusLabel('new'),
        "in-progress--outline-generating": this.getStatusLabel('in-progress--outline-generating'),
        "in-progress--outline-draft": this.getStatusLabel('in-progress--outline-draft'),
        "in-progress--content-generating": this.getStatusLabel('in-progress--content-generating'),
        "completed": this.getStatusLabel('completed'),
        "error": this.getStatusLabel('error'),
      };
    },
    filteredPublications() {
      if (!this.statusFilter && !this.searchFilter)
        return this.publications

      let results = this.publications

      if (this.statusFilter)
        results = this.filterByStatus(results)

      if (this.searchFilter)
        results = this.filterBySearch(results)

      return results
    },
    exportPublication() {
      return this.publications.find(item => item.id === this.exportPublicationID) || null
    }
  },
  methods: {
    localizeDate,
    getStatus(publication) {
      return _.get(publication, 'ai_preferences.status', "new")
    },
    getProgressPercentage(publication) {
      let val = _.get(publication, 'ai_preferences.progress', 0)
      return (val * 100)
    },
    getError(publication) {
      return _.get(publication, 'ai_preferences.message', "An unhandled error occurred during the generation process")
    },
    hasOutline(publication) {
      const pref = _.get(publication, 'ai_preferences.outline')
      return !_.isEmpty(pref)
    },
    filterBySearch(results) {
      if (!this.searchFilter)
        return results

      return results.filter(publication => {
        const search = this.searchFilter.toLowerCase()
        return publication.name.toLowerCase().includes(search)
      })
    },
    filterByStatus(results) {
      if (!this.statusFilter)
        return results

      return results.filter(publication => 
        this.getStatus(publication) === this.statusFilter
      );
    },
    isDeleteVisible(publication) {
      return this.actionMenu && this.actionMenu === publication.id
    },
    toggleActionMenu(publication) {
      if (this.actionMenu === publication.id)
        this.actionMenu = null
      else
        this.actionMenu = publication.id
    },
    isDeleteAllowed(publication) {
      if (this.hasBasicAccess)
        return false

      return !['in-progress--outline-queued', 'in-progress--outline-generating', 'in-progress--content-queued', 'in-progress--content-generating'].includes(this.getStatus(publication))
    },
    isCompleted(publication) {
      return ['complete', 'completed'].includes(this.getStatus(publication)) // TODO: cleanup and remove "complete"
    },
    getStatusClass(publication) {
      const status = this.getStatus(publication)
      switch (status) {
        case 'error':
        case 'failed':
          return 'is--error'
        case 'complete': // TODO: remove
        case 'completed':
          return 'is--success'
        case 'new':
          return 'is--info'
        case 'draft':
          return 'is--info'
        case 'in-progress--outline-queued':
          return 'is--processing'
        case 'in-progress--outline-generating':
          return 'is--processing'
        case 'in-progress--outline-draft':
          return 'is--alt'
        case 'in-progress--content-queued':
          return 'is--processing'
        case 'in-progress--content-generating':
          return 'is--processing'
        default:
          return ''
      }
    },
    getPublicationStatusLabel(publication, description = false) {
      const status = this.getStatus(publication)
      if (status === 'error' && description) // for error description retrun actual error message not static message
        return this.getError(publication)
      return this.getStatusLabel(status, description)
    },
    getStatusLabel(status, description = false) {
      switch (status) {
        case 'draft':
        case 'new':
          if (description)
            return 'New publication has been created and is ready for outline generation'
          return 'New'
        case 'in-progress--outline-queued':
          if (description)
            return 'Outline generation has been queued and will be processed shortly'
          return 'Outline Queued'
        case 'in-progress--outline-generating':
          if (description)
            return 'Outline generation is currently in progress, please wait...'
          return 'Outline Generating'
        case 'in-progress--outline-draft':
          if (description)
            return 'Outline has been generated successfully and is ready for review'
          return 'Outline Complete'
        case 'in-progress--content-queued':
          if (description)
            return 'Content generation has been queued and will be processed shortly'
          return 'Content Queued'
        case 'in-progress--content-generating':
          if (description)
            return 'Content generation is currently in progress, please wait...'
          return 'Content Generating'
        case 'complete': // TODO: remove
        case 'completed':
          if (description)
            return 'Content has been generated successfully'
          return 'Complete'
        case 'error':
        case 'failed':
          return 'Error'
      }
      return status
    },
    getCourseId(publication) {
      let courseId = _.get(publication, 'ai_preferences.coursePrefix', null)
      if (courseId)
        courseId = trim(courseId, '- ')
      return courseId || 'N/A'
    },
    hasProgressBar(publication) {
      const status = this.getStatus(publication)
      if (['in-progress--outline-queued', 'in-progress--outline-generating', 'in-progress--content-queued', 'in-progress--content-generating'].includes(status)) {
        return true
      }
      return false
    },
    onPreviewPressed(publication) {
      window.open(publication.preview_url, '_blank')
    },
    onWordPressed(publication) {
      this.exportingWord = true
      this.exportPublicationID = publication.id
      this.exportOptions = this.wordOptions
    },
    onPPTPressed(publication) {
      this.exportingPPT = true
      this.exportPublicationID = publication.id
      this.exportOptions = this.pptOptions
    },
    onScormPressed(publication) {
      this.exportingScorm = true
      this.exportPublicationID = publication.id
      this.exportOptions = this.scormOptions
    },
    onSharePressed(publication) {
      this.sharePublicPreview = true
      this.exportPublicationID = publication.id
      this.exportOptions = this.pptOptions
    },
    onExportOverlayClose() {
      this.exportingWord = false
      this.exportingPPT = false
      this.exportingScorm = false
      this.sharePublicPreview = false
      this.exportPublicationID = null
    },

    async updatePublication(event, _id)  {
      const result = await api.getPublicationsLite(this.project.id)
      if (!result.errors) {
        this.publications = result.publications
      }
      else
        console.error(result.errors)
    },
    async remove(publication) {
      if (!confirm('Are you sure you want to delete this publication?')) {
        return
      }

      // remove the element from UI
      const index = this.publications.findIndex((item) => item.id === publication.id);
      if (index !== -1)
        this.publications.splice(index, 1)

      // api call to save
      const result = await api.destroy(publication.id)

      if (result.errors) {
        // TODO: handle errors
        // catch errors
        // this.setErrors(publication.id, result);
      } else {

      }
    },
    setupEchoListener() {
      if (this.projectSource?.id) {
        Echo.private(`App.Models.ProjectSource.${this.projectSource.id}`).notification(
          (notification) => {
            const model = _.get(notification, 'model', {})

            if (notification.status === 'complete')
              window.location.reload()

            if (model.id)
              this.projectSource = this.project.project_source = model
          }
        )
      }

      if (!this.isAnalyzing)
      {
        // subscribe to channel for each course
        _.forEach(this.publications, (publication) => {
          this.setupEchoListenerForPublication(publication)
        })
      }
    },
    setupEchoListenerForPublication(publication) {
      Echo.private(`App.Models.Publication.${publication.id}`).notification(
        async (notification) => {
          const model = _.get(notification, 'model', {})
          if (model.id) {
            const index = this.publications.findIndex((item) => item.id === model.id)
            if (index === -1) return
            this.publications[index].ai_preferences = model.ai_preferences
            if (notification.progress === 1) {
              try {
                const response = await api.getPublicationData({ project: this.project, publication: model })
                this.publications.splice(index, 1, _.merge({}, this.publications[index], response.publication))
                console.log('REFRESHED PUBLICATION DATA', this.publications[index], response)
              } catch (e) {
                console.error('Failed to refresh publication data', e)
              }
            }
          }
        }
      )
    }
  },
  beforeDestroy() {
    if (this.projectSource?.id) {
      Echo.leave(`App.Models.ProjectSource.${this.projectSource.id}`)
    }
    _.forEach(this.publications, (publication) => {
      Echo.leave(`App.Models.Publication.${publication.id}`)
    })
  },
  created() {
    this.setupEchoListener()
    this.$store.commit('builder/setSchemaVersions', this.schemaVersions)
  }
}
</script>
